
import { Component, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  Action,
  Effect,
  Function as ProtoFunction,
  MCP,
  Namespace,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import {
  listMCPs$,
  listMCPTools$,
  serializeMCP$,
  upsertMCP$,
} from '@vendasta/protos/ai_assistants/v1alpha1/function-service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { switchMap } from 'rxjs';
import { AiConstraintsForm, AiToolHeaderFormArray } from '../../core/forms';
import {
  AiToolAuthStrategyForm,
  AiToolAuthStrategyFormComponent,
} from '../tools/ai-tool-auth-strategy-form/ai-tool-auth-strategy-form.component';
import { AiToolHeaderFormArrayComponent } from '../tools/ai-tool-header-form-array/ai-tool-header-form-array.component';
import { AiConstraintsFieldComponent } from '../constraints/ai-constraints-field/ai-constraints-field.component';
import { EnvironmentService } from '@galaxy/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';

export function httpUrlValidator(): ValidatorFn {
  const urlPattern =
    /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)$/;
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }
    return urlPattern.test(control.value) ? null : { invalidHttpUrl: true };
  };
}

class MCPFormControls {
  url: FormControl<string>;
  name: FormControl<string>;
  authStrategy: AiToolAuthStrategyForm;
  headers: AiToolHeaderFormArray;
  connectedIntegrationId: FormControl<string>;
  constraints: AiConstraintsForm;
  prefixToolNames: FormControl<boolean>;

  constructor(existing?: Partial<MCP>) {
    this.prefixToolNames = new FormControl(false, { nonNullable: true });
    this.url = new FormControl('', [Validators.required, httpUrlValidator()]);
    this.name = new FormControl('', [
      Validators.required,
      Validators.pattern(/^[a-zA-Z0-9_-]{1,32}$/),
      Validators.maxLength(32),
    ]);
    this.authStrategy = new AiToolAuthStrategyForm(existing?.authStrategy);
    this.headers = new AiToolHeaderFormArray([]);
    this.connectedIntegrationId = new FormControl('');
    this.constraints = new AiConstraintsForm([
      {
        action: Action.ACTION_VIEW,
        effect: Effect.EFFECT_DENY,
        scope: undefined,
      },
    ]);

    if (existing) {
      this.name.setValue(existing.id ?? '');
      this.name.disable();
      this.url.setValue(existing.url ?? '');
      this.url.disable();
      this.headers = new AiToolHeaderFormArray(existing.headers ?? []);
      this.constraints = new AiConstraintsForm(existing.constraints ?? []);
    }
  }

  valid(): boolean {
    return (!this.url.enabled || this.url.valid) && (!this.name.enabled || this.name.valid) && this.headers.valid;
  }

  get control(): FormGroup {
    return new FormGroup<any>({
      url: this.url,
      name: this.name,
      authStrategy: this.authStrategy,
      headers: this.headers,
      connectedIntegrationId: this.connectedIntegrationId,
      prefixToolNames: this.prefixToolNames,
    });
  }
}

@Component({
  selector: 'ai-mcp-import-form',
  imports: [
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    AiToolHeaderFormArrayComponent,
    GalaxyTooltip,
    MatListModule,
    MatOptionModule,
    MatSelectModule,
    MatDialogModule,
    MatStepperModule,
    AiToolAuthStrategyFormComponent,
    AiConstraintsFieldComponent,
    GalaxyAlertModule
],
  templateUrl: './ai-mcp-import-form.component.html',
  styleUrl: './ai-mcp-import-form.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class AiMcpImportFormComponent {
  protected data: {
    namespace: Namespace;
    mcp: Partial<MCP>;
  } = inject(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef<AiMcpImportFormComponent>);
  @ViewChild('stepper') stepper: MatStepper;
  private snackBar = inject(SnackbarService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private translate = inject(TranslateService);
  private env = inject(EnvironmentService);

  functions: Partial<ProtoFunction>[] = [];
  selectedFunctionIds: Set<string> = new Set();
  mcpExists = false;

  protected readonly constraintActions = [Action.ACTION_ASSIGN, Action.ACTION_VIEW];

  protected form: MCPFormControls = new MCPFormControls(this.data.mcp);

  loadFunctions(): void {
    const name = this.form.name.value;
    const url = this.form.url.value;
    const headers = this.form.headers.toFunctionHeaders();
    const authStrategy = this.form.authStrategy.authStrategy;

    listMCPs$({
      filters: {
        namespaces: [this.data.namespace],
        mcpIds: [name],
      },
      pagingOptions: { pageSize: 1 },
    })
      .pipe(
        switchMap((response) => {
          this.mcpExists = (response?.mcps?.length ?? 0) > 0;
          return listMCPTools$({
            namespace: this.data.namespace,
            mcpId: name,
            url: url,
            headers: headers,
            authStrategy: authStrategy,
          });
        }),
      )
      .subscribe({
        next: (result) => {
          this.functions = (result?.functions ?? []).map((f) => {
            if (this.form.prefixToolNames.value && f.id && !f.id.startsWith(`${name}_`)) {
              return { ...f, id: `${name}_${f.id}` };
            }
            return f;
          });
          this.selectedFunctionIds = new Set(this.functions.map((func) => func.id ?? ''));
          this.stepper.next();
        },
        error: (err) => {
          const message = err.error?.message ? err.error.message : 'Unable to load tools';
          this.snackBar.openErrorSnack(message);
        },
      });
  }

  toggleFunctionSelection(functionId: string, checked: boolean): void {
    if (checked) {
      this.selectedFunctionIds.add(functionId);
    } else {
      this.selectedFunctionIds.delete(functionId);
    }
  }

  saveMCP(): void {
    let selectedFunctions = this.functions.filter((func) => func.id && this.selectedFunctionIds.has(func.id));
    const authStrategy = this.form.authStrategy.authStrategy;
    const constraints = this.form.constraints.toConstraints();
    selectedFunctions = selectedFunctions.map((func) => ({ ...func, constraints }) as Partial<ProtoFunction>);

    const upsertRequest$ = upsertMCP$({
      namespace: this.data.namespace,
      mcpId: this.form.name.value,
      url: this.form.url.value,
      functions: selectedFunctions as ProtoFunction[],
      authStrategy: authStrategy,
      headers: this.form.headers.toFunctionHeaders(),
      constraints: this.form.constraints.toConstraints(),
    });

    if (this.mcpExists) {
      this.confirmationModal
        .openModal({
          type: 'warn',
          title: this.translate.instant('AI_ASSISTANT.MCP.CONFIRMATION_TITLE'),
          message: this.translate.instant('AI_ASSISTANT.MCP.ALREADY_EXISTS'),
          confirmButtonText: this.translate.instant('AI_ASSISTANT.MCP.UPDATE_MCP'),
          cancelButtonText: this.translate.instant('AI_ASSISTANT.SHARED.CANCEL'),
        })
        .subscribe((confirmed) => {
          if (confirmed) {
            upsertRequest$.subscribe({
              next: () => {
                this.snackBar.openSuccessSnack(this.translate.instant('AI_ASSISTANT.MCP.TOOLS_SAVED'));
                this.dialogRef.close();
              },
              error: (err) => {
                const message = err.error?.message ? err.error.message : 'An error occurred while saving tools';
                this.snackBar.openErrorSnack(message);
              },
            });
          }
        });
      return;
    } else {
      upsertRequest$.subscribe({
        next: () => {
          this.snackBar.openSuccessSnack(this.translate.instant('AI_ASSISTANT.MCP.TOOLS_SAVED'));
          this.dialogRef.close();
        },
        error: (err) => {
          const message = err.error?.message ? err.error.message : 'An error occurred while saving tools';
          this.snackBar.openErrorSnack(message);
        },
      });
    }
  }

  exportAndClose(): void {
    this.export();
    this.dialogRef.close();
  }

  export(): void {
    const fileName = `${this.form.name.value}.${this.env.getEnvironmentString()}`;
    serializeMCP$({
      mcp: {
        id: this.form.name.value,
        url: this.form.url.value,
        authStrategy: this.form.authStrategy.authStrategy,
        headers: this.form.headers.toFunctionHeaders(),
        constraints: this.form.constraints.toConstraints(),
      } as MCP,
    })
      .subscribe({
        next: (response) => {
          downloadYaml(response.file, fileName);
        },
        error: (err) => {
          const message = err.error?.message ? err.error.message : 'An error occurred while exporting MCP';
          this.snackBar.openErrorSnack(message);
        },
      });
  }
}

function downloadYaml(yamlData: string, filename: string): void {
  const blob = new Blob([yamlData], { type: 'application/yaml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.yaml`;
  a.click();
  URL.revokeObjectURL(url);
}
